/**
 * Paints the native Android status bar (top) and navigation bar (bottom)
 * with the user's active theme color. Runs only inside the Capacitor APK —
 * on the web/PWA it's a no-op (the browser already handles theme-color via
 * the <meta name="theme-color"> tag).
 *
 * Plugins used (both installed ad-hoc in the GitHub Actions Android build):
 *   - @capacitor/status-bar          (top status bar)
 *   - @capgo/capacitor-navigation-bar (bottom Android navigation bar)
 *
 * We access them through window.Capacitor.Plugins so the web bundle never
 * needs to import them — keeps Emergent web builds clean.
 */

const isNative = () => {
  if (typeof window === 'undefined') return false;
  return !!window.Capacitor?.isNativePlatform?.();
};

// Determine if text/icons on the bar should be dark (for light bg) or light (for dark bg).
// Uses a quick luminance calc on the hex.
const isLightColor = (hex) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  // Perceived brightness
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6;
};

export const setNativeChromeColor = async (hex) => {
  if (!isNative()) return;
  if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return;

  const plugins = window.Capacitor?.Plugins || {};
  const light = isLightColor(hex);

  // Android 15 edge-to-edge: this is the ONLY plugin that reliably paints
  // the status bar on Android 15 / One UI 7+ because Android ignores
  // statusBarColor / @capacitor/status-bar setBackgroundColor in edge-to-edge.
  if (plugins.EdgeToEdge) {
    try {
      await plugins.EdgeToEdge.setBackgroundColor({ color: hex });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[native-chrome] EdgeToEdge set failed', e);
    }
  }

  // Top status bar — kept as fallback for Android <15 where it still works
  if (plugins.StatusBar) {
    try {
      await plugins.StatusBar.setOverlaysWebView({ overlay: false });
      await plugins.StatusBar.setBackgroundColor({ color: hex });
      // 'LIGHT' = light content (use on dark bg), 'DARK' = dark content (use on light bg)
      await plugins.StatusBar.setStyle({ style: light ? 'DARK' : 'LIGHT' });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[native-chrome] StatusBar set failed', e);
    }
  }

  // Bottom Android navigation bar
  if (plugins.NavigationBar) {
    try {
      await plugins.NavigationBar.setNavigationBarColor({ color: hex, darkButtons: light });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[native-chrome] NavigationBar set failed', e);
    }
  }
};
